import * as React from "react";
import { MAX_PERSONA_SCRIPT_SLOT_INDEX } from "@nexus/contracts";
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
} from "@nexus/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import {
  legacyOpportunitySetScriptsInfiniteQueryOptions,
  legacyOpportunitySetsQueryOptions,
} from "@/lib/queries";

const SEARCH_DEBOUNCE_MS = 300;

export interface PersonaImportLegacyPaneProps {
  open: boolean;
  tenantId: string;
  disabled?: boolean;
  selectedIds: ReadonlySet<string>;
  maxSelectable: number;
  onSetChange: (legacySetId: string) => void;
  onToggleId: (scriptId: string) => void;
  onUsePaste?: () => void;
}

const MODAL_EMPTY_CLASS =
  "flex-1 py-6 [&_h3]:text-[15px] [&_p]:max-w-[360px] [&_p]:text-[13px] [&_p]:leading-relaxed";

function scriptTitle(
  script: { hook: string | null; concept: string | null },
  untitled: string,
): string {
  const hook = script.hook?.trim();
  if (hook) return hook;
  const concept = script.concept?.trim();
  if (concept) return concept;
  return untitled;
}

export function PersonaImportLegacyPane({
  open,
  tenantId,
  disabled,
  selectedIds,
  maxSelectable,
  onSetChange,
  onToggleId,
  onUsePaste,
}: PersonaImportLegacyPaneProps) {
  const { t } = useTranslation();
  const [legacySetId, setLegacySetId] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const { debounced: debounceSearch, cancel: cancelSearch } = useDebouncedCallback(
    (value: string) => setDebouncedSearch(value),
    SEARCH_DEBOUNCE_MS,
  );
  const handleSetChange = React.useCallback(
    (nextSetId: string) => {
      setLegacySetId(nextSetId);
      setSearchInput("");
      setDebouncedSearch("");
      cancelSearch();
      onSetChange(nextSetId);
    },
    [cancelSearch, onSetChange],
  );

  React.useEffect(() => {
    if (!open) return;
    setLegacySetId("");
    setSearchInput("");
    setDebouncedSearch("");
    cancelSearch();
  }, [open, cancelSearch]);

  const setsQuery = useQuery(
    legacyOpportunitySetsQueryOptions(tenantId, { enabled: open }),
  );
  const sets = setsQuery.data?.items ?? [];
  const firstSetId = sets[0]?.id;

  React.useEffect(() => {
    if (!legacySetId && firstSetId) handleSetChange(firstSetId);
  }, [legacySetId, firstSetId, handleSetChange]);

  const scriptsQuery = useInfiniteQuery(
    legacyOpportunitySetScriptsInfiniteQueryOptions(tenantId, legacySetId, debouncedSearch, {
      enabled: open && Boolean(legacySetId),
    }),
  );
  const scripts = scriptsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const untitled = t("personaOpportunities.scripts.import.legacyUntitled", "Untitled script");
  const hasSearch = debouncedSearch.trim().length > 0;
  const atSelectionLimit = selectedIds.size >= maxSelectable;
  const remainingSelectable = Math.max(0, maxSelectable - selectedIds.size);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="persona-import-legacy-pane">
      {setsQuery.isPending ? (
        <Skeleton className="h-8 w-full" />
      ) : sets.length === 0 ? (
        <EmptyState
          icon="folder"
          className={MODAL_EMPTY_CLASS}
          title={t("personaOpportunities.scripts.import.legacyEmptySets", "No Opportunities sets")}
          description={t(
            "personaOpportunities.scripts.import.legacyEmptySetsDescription",
            "This tenant has no legacy sets to copy from. Paste a script or upload a file instead.",
          )}
          action={
            onUsePaste ? (
              <Button
                type="button"
                variant="outline"
                className="h-9 px-3.5"
                data-testid="persona-import-legacy-use-paste"
                onClick={onUsePaste}
              >
                {t("personaOpportunities.scripts.import.legacyUsePaste", "Paste or upload a file")}
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona-import-legacy-set" className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {t("personaOpportunities.scripts.import.legacySetLabel", "Opportunity set")}
            </Label>
            <Select
              value={legacySetId}
              onValueChange={handleSetChange}
              disabled={disabled}
            >
              <SelectTrigger
                id="persona-import-legacy-set"
                data-testid="persona-import-legacy-set"
                aria-label={t("personaOpportunities.scripts.import.legacySetLabel", "Opportunity set")}
              >
                <SelectValue
                  placeholder={t(
                    "personaOpportunities.scripts.import.legacySetPlaceholder",
                    "Select a set",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {sets.map((set) => (
                  <SelectItem key={set.id} value={set.id}>
                    {set.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            data-testid="persona-import-legacy-search"
            value={searchInput}
            disabled={disabled}
            iconStart="search"
            aria-label={t("personaOpportunities.scripts.import.legacySearch", "Search scripts")}
            placeholder={t("personaOpportunities.scripts.import.legacySearch", "Search scripts")}
            onChange={(event) => {
              const next = event.target.value;
              setSearchInput(next);
              debounceSearch(next);
            }}
          />
          {scriptsQuery.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : scripts.length === 0 ? (
            <EmptyState
              icon="search"
              className={MODAL_EMPTY_CLASS}
              title={
                hasSearch
                  ? t(
                      "personaOpportunities.scripts.import.legacyEmptySearch",
                      "No scripts match that search",
                    )
                  : t(
                      "personaOpportunities.scripts.import.legacyEmptyScripts",
                      "This set has no scripts",
                    )
              }
              description={
                hasSearch
                  ? t(
                      "personaOpportunities.scripts.import.legacyEmptySearchDescription",
                      "Try a different search.",
                    )
                  : t(
                      "personaOpportunities.scripts.import.legacyEmptyScriptsDescription",
                      "Pick another set, or paste a script instead.",
                    )
              }
            />
          ) : (
            <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
              {scripts.map((script) => {
                const checked = selectedIds.has(script.id);
                const title = scriptTitle(script, untitled);
                const selectionLocked = !checked && atSelectionLimit;
                const checkboxDisabled = Boolean(disabled) || selectionLocked;
                return (
                  <li key={script.id}>
                    <label
                      className={cn(
                        "flex items-start gap-2.5 rounded-sm px-2 py-2 hover:bg-muted/60",
                        selectionLocked ? "cursor-not-allowed" : "cursor-pointer",
                      )}
                      data-testid={`persona-import-legacy-script-${script.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={checkboxDisabled}
                        className="mt-px"
                        aria-label={title}
                        onCheckedChange={() => {
                          if (checkboxDisabled) return;
                          onToggleId(script.id);
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-foreground">{title}</span>
                        {script.concept?.trim() && script.concept.trim() !== title ? (
                          <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">
                            {script.concept}
                          </span>
                        ) : script.script?.trim() ? (
                          <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">
                            {script.script}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {scriptsQuery.hasNextPage ? (
            <Button
              type="button"
              variant="ghost"
              className="h-8 self-start px-2 text-xs"
              data-testid="persona-import-legacy-load-more"
              disabled={disabled || scriptsQuery.isFetchingNextPage}
              onClick={() => void scriptsQuery.fetchNextPage()}
            >
              {t("personaOpportunities.scripts.import.legacyLoadMore", "Load more")}
            </Button>
          ) : null}
          <p
            data-testid="persona-import-legacy-reimport-note"
            className="shrink-0 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground"
          >
            {t(
              "personaOpportunities.scripts.import.legacyReimportNote",
              "Importing a script you already brought in adds another copy.",
            )}
          </p>
          {selectedIds.size > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("personaOpportunities.scripts.import.legacySelected", {
                count: selectedIds.size,
                defaultValue_one: "{{count}} script selected",
                defaultValue_other: "{{count}} scripts selected",
              })}
            </p>
          ) : null}
          <p
            data-testid="persona-import-legacy-slots-remaining"
            className="text-xs text-muted-foreground"
          >
            {t("personaOpportunities.scripts.import.slotsRemaining", {
              count: remainingSelectable,
              max: MAX_PERSONA_SCRIPT_SLOT_INDEX,
              defaultValue_one: "{{count}} of {{max}} slots remaining",
              defaultValue_other: "{{count}} of {{max}} slots remaining",
            })}
          </p>
        </>
      )}
    </div>
  );
}
